import React, { useEffect, useMemo, useRef } from "react"
import { FileText, Upload } from "lucide-react"
import Dropzone from "dropzone"

function buildLicensePreviewTemplate() {
    return `
        <div class="row py-3 px-2 g-2 align-items-center">
            <div class="col-auto">
                <span class="preview d-block">
                    <img data-dz-thumbnail style="width:48px;height:48px;object-fit:cover;border-radius:6px;" />
                </span>
            </div>
            <div class="col" style="min-width:0;">
                <div class="text-break small fw-semibold" data-dz-name></div>
                <strong class="error text-danger small" data-dz-errormessage></strong>
            </div>
            <div class="col-auto text-muted small" data-dz-size></div>
            <div class="col-auto">
                <button data-dz-remove type="button" class="btn btn-light btn-sm">
                    Remove
                </button>
            </div>
        </div>
    `
}

function pickPrimaryLicenseFile(next) {
    return next.licenseFileFront ?? next.licenseFileBack ?? null
}

export function setLicenseFileSide(value, side, file) {
    const next = { ...value }
    if (side === "front") {
        next.licenseFileFront = file
        if (file == null) {
            next.licenseFrontImg = null
        }
    } else {
        next.licenseFileBack = file
        if (file == null) {
            next.licenseBackImg = null
        }
    }
    next.licenseFile = pickPrimaryLicenseFile(next)
    return next
}

/**
 * Front/back fishing license dropzones (Dropzone.js).
 * @param {{
 *   value: {
 *     licenseFileFront?: File | null,
 *     licenseFileBack?: File | null,
 *     licenseFile?: File | null,
 *   },
 *   onChange: (next: Record<string, unknown>) => void,
 *   label?: string,
 *   frontLabelExtra?: import("react").ReactNode,
 *   backLabelExtra?: import("react").ReactNode,
 * }} props
 */
export default function LicensePhotosUpload({
    value,
    onChange,
    label = "Fishing License Photos (optional)",
    frontLabelExtra = null,
    backLabelExtra = null,
}) {
    const valueRef = useRef(value)
    valueRef.current = value
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange
    const dropzoneFrontRootRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const dropzoneBackRootRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const dzFrontRef = useRef(/** @type {any} */(null))
    const dzBackRef = useRef(/** @type {any} */(null))
    const suppressAddedFrontRef = useRef(false)
    const suppressAddedBackRef = useRef(false)
    const previewTemplate = useMemo(() => buildLicensePreviewTemplate(), [])

    useEffect(() => {
        function initSide(side, rootRef, dzRef) {
            const root = rootRef.current
            if (!root) {
                return null
            }
            if (dzRef.current) {
                return dzRef.current
            }

            const dz = new Dropzone(root, {
                autoProcessQueue: false,
                url: "/api/orders/license",
                maxFiles: 1,
                resizeWidth: 1000,
                acceptedFiles: "image/*,application/pdf",
                addRemoveLinks: false,
                clickable: root.querySelector("[data-license-clickable]") ?? undefined,
                previewTemplate,
                previewsContainer: root.querySelector("[data-license-preview]") ?? undefined,
            })

            dz.on("addedfile", function (file) {
                if (side === "front" && suppressAddedFrontRef.current) {
                    suppressAddedFrontRef.current = false
                    return
                }
                if (side === "back" && suppressAddedBackRef.current) {
                    suppressAddedBackRef.current = false
                    return
                }
                onChangeRef.current(setLicenseFileSide(valueRef.current, side, file))
            })

            dz.on("removedfile", function () {
                onChangeRef.current(setLicenseFileSide(valueRef.current, side, null))
            })

            dzRef.current = dz
            return dz
        }

        initSide("front", dropzoneFrontRootRef, dzFrontRef)
        initSide("back", dropzoneBackRootRef, dzBackRef)

        return () => {
            for (const dzRef of [dzFrontRef, dzBackRef]) {
                const dz = dzRef.current
                if (!dz) continue
                try {
                    dz.destroy()
                } finally {
                    dzRef.current = null
                }
            }
        }
    }, [previewTemplate])

    useEffect(() => {
        const dz = dzFrontRef.current
        const file = value?.licenseFileFront
        if (!dz || !(file instanceof File)) {
            return
        }
        const existing = Array.isArray(dz.files) ? dz.files : []
        if (
            existing.some(
                (f) =>
                    f === file ||
                    (f?.name === file.name &&
                        f?.size === file.size &&
                        f?.lastModified === file.lastModified)
            )
        ) {
            return
        }
        suppressAddedFrontRef.current = true
        dz.addFile(file)
    }, [value?.licenseFileFront])

    useEffect(() => {
        const dz = dzBackRef.current
        const file = value?.licenseFileBack
        if (!dz || !(file instanceof File)) {
            return
        }
        const existing = Array.isArray(dz.files) ? dz.files : []
        if (
            existing.some(
                (f) =>
                    f === file ||
                    (f?.name === file.name &&
                        f?.size === file.size &&
                        f?.lastModified === file.lastModified)
            )
        ) {
            return
        }
        suppressAddedBackRef.current = true
        dz.addFile(file)
    }, [value?.licenseFileBack])

    return (
        <div className="col-12">
            <label className="form-label">{label}</label>
            <div className="row g-3">
                {[
                    {
                        key: "front",
                        sideLabel: "Front",
                        rootRef: dropzoneFrontRootRef,
                        file: value?.licenseFileFront,
                        labelExtra: frontLabelExtra,
                    },
                    {
                        key: "back",
                        sideLabel: "Back",
                        rootRef: dropzoneBackRootRef,
                        file: value?.licenseFileBack,
                        labelExtra: backLabelExtra,
                    },
                ].map(({ key, sideLabel, rootRef, file, labelExtra }) => (
                    <div key={key} className="col-12 col-md-6">
                        <div className="form-label fw-semibold mb-2" style={{ fontSize: "0.875rem" }}>
                            {sideLabel}
                            {labelExtra}
                        </div>
                        <div
                            ref={rootRef}
                            className="d-flex flex-column align-items-center justify-content-center p-4 border border-2 border-dashed rounded border-secondary"
                            style={{ backgroundColor: "#f8f9fa", minHeight: "150px" }}
                        >
                            <div
                                data-license-clickable
                                className={
                                    (file ? "d-none " : "") +
                                    "order-form-dropzone-clickable d-flex flex-column align-items-center justify-content-center"
                                }
                            >
                                <Upload className="text-primary mb-2" size={32} />
                                <span className="text-muted fw-semibold small">
                                    Upload {sideLabel} of License
                                </span>
                                <span
                                    className="text-muted small mt-1"
                                    style={{ fontSize: "0.75rem" }}
                                >
                                    Click or drag and drop
                                </span>
                                <div className="d-none">
                                    <FileText />
                                </div>
                            </div>
                            <div data-license-preview className="preview dropzone-preview w-100" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}
